import './shareholder-documents.scss';

import BackDrop from 'components/backdrop/backdrop';
import ModalAction from 'components/modal-action/modal-action';
import ShareholderDocumentsTableHeader, { ShareholderDocumentsSortProperties } from 'features/shareholder-documents/components/shareholder-documents-table-header/shareholder-documents-table-header';
import ShareholderDocumentTable from 'features/shareholder-documents/components/shareholder-documents-table/shareholder-documents';
import { HttpRequestStatus } from 'model/enums/httpRequestStatus';
import { ShareholderDocumentsToGetAllRequest } from 'model/shareholder-documents.model';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from 'reactstrap';
import { useDownloadShareholderDocumentState, useGetShareholderDocumentsState, useRootDispatch, useUserAccountState } from 'reducer/hooks';
import { downloadShareholderDocumentRequest, downloadShareholderDocumentResetState } from 'reducer/shareholder/download-shareholder-document/actions';
import { getShareholderDocumentsRequest, getShareholderDocumentsResetState } from 'reducer/shareholder/get-shareholder-documents/actions';
import { LIST_PAGE_SIZE } from 'services/pageable';
import { SortOrder } from 'services/sort';
import { useToast } from 'shared/hooks/use-toast';
import Pagination from 'shared/pagination/pagination';

const I18N_PREFIX = 'shareholder-documents';

const ShareholderDocuments = () => {
    const [page, setPage] = useState<number>(0);
    const [sortedProperty, setSortedProperty] = useState<ShareholderDocumentsSortProperties | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<SortOrder>(undefined);
    const [selectedDocumentId, setSelectedDocumentId] = useState<number | undefined>(undefined);

    const { t } = useTranslation();
    const { toastSuccess } = useToast();

    const { status: downloadStatus } = useDownloadShareholderDocumentState();

    const handlePageChange = (value: { selected: number }) => {
        setPage(value.selected);
    };

    const handleSort = (property: ShareholderDocumentsSortProperties, order: SortOrder) => {
        if (property !== sortedProperty) {
            setSortedProperty(property);
            setSortOrder('asc');
        } else if (order === 'asc') {
            setSortedProperty(property);
            setSortOrder('desc');
        } else if (order === 'desc') {
            setSortedProperty(undefined);
            setSortOrder(undefined);
        } else {
            setSortedProperty(property);
            setSortOrder('asc');
        }
    };

    const { account } = useUserAccountState();

    const personId: string | undefined = useMemo(() => account?.personId, [account]);

    const sortPageable = useCallback((): ShareholderDocumentsToGetAllRequest => {
        const pageable = {
            page,
            size: LIST_PAGE_SIZE,
            sort: sortedProperty && sortOrder ? `${sortedProperty},${sortOrder}` : ''
        };

        return { ...pageable, personId };
    }, [sortedProperty, sortOrder, page, personId]);

    const dispatch = useRootDispatch();

    const handleShareholderDocumentsList = useCallback(() => {
        dispatch(getShareholderDocumentsRequest(sortPageable()));
    }, [dispatch, sortPageable]);

    useEffect(() => {
        handleShareholderDocumentsList();
    }, [handleShareholderDocumentsList]);

    const handleDocumentDownload = useCallback(() => {
        if (!selectedDocumentId) return;

        dispatch(downloadShareholderDocumentRequest(selectedDocumentId));
    }, [selectedDocumentId, dispatch]);

    const handleCancelModal = useCallback(() => {
        setSelectedDocumentId(undefined);
    }, []);

    useEffect(() => {
        if (HttpRequestStatus.SUCCESS !== downloadStatus) return;

        toastSuccess(t(`${I18N_PREFIX}.download.success`));
        handleCancelModal();
        dispatch(downloadShareholderDocumentResetState());
    }, [downloadStatus, toastSuccess, t, handleCancelModal, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(getShareholderDocumentsResetState());
            dispatch(downloadShareholderDocumentResetState());
        };
    }, [dispatch]);

    const { shareholderDocuments, status } = useGetShareholderDocumentsState();

    const isTableLoading: boolean = HttpRequestStatus.ONGOING === status;
    const isDocumentDownloading: boolean = HttpRequestStatus.ONGOING === downloadStatus;

    return (
        <>
            {<BackDrop open={isDocumentDownloading} />}
            <main className="shareholder-documents">
                <Container>
                    <h1 className="shareholder-documents--title">{t(`${I18N_PREFIX}.title`)}</h1>
                    <section className="shareholder-documents--container">
                        <table className="page-container--table">
                            <thead>
                                <ShareholderDocumentsTableHeader shareholderDocuments={shareholderDocuments?.content ?? []} handleSort={handleSort} sortOrder={sortOrder} sortedProperty={sortedProperty} />
                            </thead>
                            <tbody>
                                <ShareholderDocumentTable shareholderDocuments={shareholderDocuments?.content ?? []} isLoading={isTableLoading} setSelectedDocumentId={setSelectedDocumentId} />
                            </tbody>
                        </table>
                        {!isTableLoading && <Pagination page={page} totalPages={shareholderDocuments?.totalPages ?? 0} onChange={handlePageChange} />}
                    </section>
                </Container>
                {selectedDocumentId && <ModalAction title={t(`${I18N_PREFIX}.download.title`)} message={t(`${I18N_PREFIX}.download.message`)} onClose={handleCancelModal} onAction={handleDocumentDownload} />}
            </main>
        </>
    );
};

export default ShareholderDocuments;
